import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { FileText, Shuffle } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ArticleGrid } from '@/components/ArticleGrid';
import { LoadMore } from '@/components/LoadMore';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAllWikiArticles } from '@/hooks/useWikiArticles';
import { articleTitle, getTagValue } from '@/lib/nip54';
import { cn } from '@/lib/utils';

const LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)); // A–Z

/**
 * Maps an article title to its index letter: A–Z with diacritics folded
 * (É → E), or `#` for digits, symbols, and non-Latin scripts.
 */
function titleLetter(title: string): string {
  const first = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .charAt(0)
    .toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : '#';
}

/** One letter of the A–Z index — a toggle button when articles exist for it, dimmed placeholder otherwise. */
function LetterButton({
  letter,
  enabled,
  active,
  onToggle,
}: {
  letter: string;
  enabled: boolean;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!enabled}
      onClick={onToggle}
      aria-pressed={active}
      title={letter === '#' ? 'Titles not starting with A–Z' : `Articles starting with ${letter}`}
      className={cn(
        'size-7 rounded text-center text-sm transition-colors',
        active
          ? 'bg-primary font-medium text-primary-foreground'
          : enabled
            ? 'text-muted-foreground hover:bg-accent hover:text-foreground'
            : 'cursor-default text-muted-foreground/40',
      )}
    >
      {letter}
    </button>
  );
}

/**
 * "All articles" (`/articles`) — the paginated continuation of the
 * homepage's Recent tab. Every wiki article your relays know about,
 * newest first, loading older history as the reader scrolls. Reached via
 * the "All articles" link under the Recent tab and from the footer.
 *
 * The A–Z row under the heading filters the list by first letter.
 * Relays can't prefix-search titles (the `d` tag is exact-match only),
 * so the index is client-side over the loaded set: letters with no
 * matching article stay dimmed, and paging further back through history
 * can light up more letters and add matches to the active one.
 */
export function AllArticlesPage() {
  const { articles, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useAllWikiArticles();
  const [letter, setLetter] = useState<string | undefined>();
  const navigate = useNavigate();

  useSeoMeta({
    title: 'All articles — Wikistar',
    description: 'Every wiki article on Nostr, newest first — every author\u2019s version side by side.',
  });

  // Letters present among the loaded articles — recomputed as pages arrive.
  const available = useMemo(() => {
    const letters = new Set<string>();
    for (const event of articles) letters.add(titleLetter(articleTitle(event)));
    return letters;
  }, [articles]);

  const filtered = letter ? articles.filter((event) => titleLetter(articleTitle(event)) === letter) : articles;

  /** Opens a random article from the currently visible set — honoring the active letter filter. */
  const openRandom = () => {
    if (filtered.length === 0) return;
    const event = filtered[Math.floor(Math.random() * filtered.length)];
    const slug = getTagValue(event, 'd') ?? '';
    navigate(`/w/${encodeURIComponent(slug)}/${nip19.npubEncode(event.pubkey)}`);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-3 flex items-center gap-2">
          <FileText className="size-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">All articles</h1>
        </div>

        <nav aria-label="Filter articles by first letter" className="mb-6 flex flex-wrap items-center gap-x-0.5 gap-y-1">
          <button
            type="button"
            onClick={() => setLetter(undefined)}
            aria-pressed={letter === undefined}
            className={cn(
              'rounded px-1.5 py-0.5 text-sm transition-colors',
              letter === undefined ? 'bg-primary font-medium text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            All
          </button>
          {[...LETTERS, '#'].map((l) => (
            <LetterButton
              key={l}
              letter={l}
              enabled={available.has(l)}
              active={letter === l}
              onToggle={() => setLetter(letter === l ? undefined : l)}
            />
          ))}
          <button
            type="button"
            onClick={openRandom}
            disabled={filtered.length === 0}
            title={letter ? `Open a random article starting with ${letter}` : 'Open a random article'}
            className="ml-1 inline-flex items-center gap-1 rounded border-l border-border px-1.5 py-0.5 pl-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-default disabled:opacity-40"
          >
            <Shuffle className="size-3.5" />
            Random
          </button>
        </nav>

        <ArticleGrid
          articles={filtered}
          isLoading={isLoading}
          skeletonCount={9}
          emptyMessage="No articles found yet. Try checking your relay connections, or be the first to write one!"
        />

        <LoadMore
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          loadingContent={
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="space-y-3 pt-6">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </CardContent>
                </Card>
              ))}
            </div>
          }
          endContent={
            articles.length > 10 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                You've reached the beginning — that's every article your relays know about.
              </p>
            ) : undefined
          }
        />
      </div>
    </Layout>
  );
}
